// Owned by mju-ai-workbench/shared/apps/phoneControls.ts — how the phone draws every node type of an app (D354). The AIOE session mirrors it for the companion app's `<AppView>`; the workbench never mirrors it back. Keep the two identical.
/**
 * The phone draws the same apps as the desktop. The companion app (AIOE's Expo / React Native app) walks an app's `UiSpecV1`
 * (shared/schemas/uiSpec.ts) with a registry `Record<nodeType, (node, ctx) => ReactNode>`; this file says, for EVERY node type, what
 * the entry is:
 *
 *  - `native`: the phone's native component that draws it, when one exists (from the packages `heroui-native` and
 *    `heroui-native-pro` — the latter imported by subpath, e.g. `heroui-native-pro/carousel`; names checked by the AIOE session
 *    against heroui-native 1.0.11 and heroui-native-pro 1.0.0-beta.11 on 26 Oct 2026; a name that is not there falls to the
 *    `fallback` below, so nothing is ever blank),
 *  - `fallback`: what the phone draws instead when there is no native counterpart: always something readable, never nothing.
 *
 * An unknown node type (a build newer than the phone) is drawn as {@link PHONE_UNKNOWN_NODE}; a spec version the phone does not
 * know is drawn as the card only ({@link PHONE_UNKNOWN_SPEC}). A custom view (HTML) is drawn in a sandboxed WebView with the bridge
 * of shared/apps/customViewBridge.ts and the same content policy; its contract is {@link PHONE_CUSTOM_VIEW}.
 *
 * Calls the phone makes (Control API over the relay, scopes the app declared): `apps.list`, `apps.get { appId }`,
 * `apps.read { appId, read, params? }`, `apps.act { appId, action, input? }` (shared/schemas/companion.ts). The feed card of an app
 * opens `{ view: "apps", id: <appId> }` (`open.phone`), which the phone routes to `/app/[deviceId]/[appId]`.
 *
 * Renderer-neutral: nothing here names a web-only prop. Pure data: no imports but the node-type lists.
 */
import { BASE_NODE_TYPES } from "../schemas/uiSpec.js";
import { CONTROL_DEFS } from "../schemas/uiControls/index.js";
const n = (native, fallback) => ({ native, from: "heroui-native", fallback });
const pro = (native, fallback) => ({ native, from: "heroui-native-pro", fallback });
const f = (fallback) => ({ fallback });
/** Every node type of `UiSpecV1`, the phone's way. A test fails when a type has no entry or an entry has no type. */
export const PHONE_CONTROLS = {
    // The first eighteen
    stack: f("children"),
    text: f("text"),
    heading: f("text"),
    divider: n("Separator", "none"),
    card: n("Card", "children"),
    "choice-cards": n("RadioGroup", "options"),
    "field-text": n("TextField", "text-field"),
    "field-select": n("Select", "options"),
    "field-number": pro("NumberField", "text-field"),
    "field-toggle": n("Switch", "label-and-value"),
    table: pro("Table", "list"),
    stat: f("label-and-value"),
    progress: pro("ProgressBar", "label-and-value"),
    steps: pro("Stepper", "list"),
    chip: n("Chip", "text"),
    button: n("Button", "text"),
    actions: f("children"),
    link: n("Button", "text"),
    // Data display
    "data-grid": pro("Table", "list"),
    "list-view": n("ListGroup", "list"),
    "item-card": n("Card", "label-and-value"),
    "item-card-group": f("children"),
    kpi: f("label-and-value"),
    "kpi-group": f("children"),
    widget: pro("Widget", "children"),
    timeline: pro("Timeline", "list"),
    agenda: pro("Agenda", "list"),
    kanban: f("lists-by-column"),
    "file-tree": f("list"),
    "empty-state": pro("EmptyState", "text"),
    carousel: pro("Carousel", "list"),
    "hover-card": f("button-opens-sheet"),
    "holo-card": n("Card", "children"),
    "trend-chip": pro("TrendChip", "text"),
    "number-value": pro("NumberValue", "label-and-value"),
    rating: pro("Rating", "label-and-value"),
    meter: pro("ProgressBar", "label-and-value"),
    "progress-circle": pro("ProgressCircle", "label-and-value"),
    spinner: n("Spinner", "text"),
    skeleton: n("Skeleton", "none"),
    alert: n("Alert", "text"),
    badge: pro("Badge", "text"),
    avatar: n("Avatar", "text"),
    "avatar-group": f("list"),
    kbd: f("text"),
    tooltip: f("children"),
    typography: f("text"),
    markdown: f("text"),
    "code-block": f("text"),
    "chain-of-thought": f("list"),
    "text-shimmer": f("text"),
    "color-swatch": f("label-and-value"),
    // Charts and data visualisation: a small table plus the headline figure
    "area-chart": pro("AreaChart", "table-and-headline"),
    "bar-chart": pro("BarChart", "table-and-headline"),
    "line-chart": pro("LineChart", "table-and-headline"),
    "composed-chart": pro("ComposedChart", "table-and-headline"),
    "pie-chart": pro("PieChart", "table-and-headline"),
    "radar-chart": pro("RadarChart", "table-and-headline"),
    "radial-chart": pro("RadialChart", "table-and-headline"),
    "scatter-chart": f("table-and-headline"),
    "bubble-chart": f("table-and-headline"),
    histogram: f("table-and-headline"),
    "box-plot": f("table-and-headline"),
    "funnel-chart": f("table-and-headline"),
    treemap: f("table-and-headline"),
    heatmap: f("table-and-headline"),
    sankey: f("table-and-headline"),
    gauge: f("label-and-value"),
    sparkline: f("table-and-headline"),
    "waterfall-chart": f("table-and-headline"),
    // Forms
    "text-area": n("TextArea", "text-area"),
    "search-field": n("SearchField", "text-field"),
    "input-group": n("InputGroup", "text-field"),
    "number-field": pro("NumberField", "text-field"),
    "number-stepper": pro("NumberStepper", "text-field"),
    select: n("Select", "options"),
    autocomplete: pro("Autocomplete", "options"),
    "combo-box": pro("ComboBox", "options"),
    "inline-select": f("options"),
    "native-select": f("options"),
    "cell-select": f("options"),
    checkbox: n("Checkbox", "label-and-value"),
    "checkbox-group": n("Checkbox", "options"),
    "checkbox-button-group": f("options"),
    "radio-group": n("RadioGroup", "options"),
    "radio-button-group": pro("RadioButtonGroup", "options"),
    switch: n("Switch", "label-and-value"),
    "cell-switch": n("Switch", "label-and-value"),
    slider: n("Slider", "text-field"),
    "cell-slider": n("Slider", "text-field"),
    segment: pro("Segment", "options"),
    "date-field": pro("DateField", "text-field"),
    "date-picker": pro("DatePicker", "text-field"),
    "date-range-picker": pro("DateRangePicker", "text-field"),
    "time-field": pro("TimePicker", "text-field"),
    calendar: pro("Calendar", "text-field"),
    "range-calendar": pro("RangeCalendar", "text-field"),
    "tag-group": n("TagGroup", "options"),
    "color-field": f("text-field"),
    "color-picker": f("text-field"),
    "color-swatch-picker": f("options"),
    "cell-color-picker": f("text-field"),
    "rich-text-editor": f("text-area"),
    "drop-zone": f("desktop-note"),
    // Navigation inside an app, and overlays
    tabs: n("Tabs", "children"),
    accordion: n("Accordion", "children"),
    disclosure: f("children"),
    "disclosure-group": f("children"),
    breadcrumbs: f("text"),
    pagination: f("label-and-value"),
    stepper: pro("Stepper", "list"),
    toolbar: f("children"),
    "button-group": f("children"),
    "toggle-button": pro("ToggleButton", "label-and-value"),
    "toggle-button-group": pro("ToggleButtonGroup", "options"),
    dropdown: n("Menu", "button-opens-sheet"),
    "action-bar": f("children"),
    sheet: n("BottomSheet", "button-opens-sheet"),
    modal: n("Dialog", "button-opens-sheet"),
    // Map: the phone's native package has no map yet (heroui-native-pro 1.0.0-beta.11), so the places are drawn as a list.
    map: f("list-of-places"),
};
/** An unknown node type: a quiet line, never a blank. */
export const PHONE_UNKNOWN_NODE = "Open on the workbench to see this part";
/** A spec version the phone does not know: the card only (icon, name, purpose), with the same line. */
export const PHONE_UNKNOWN_SPEC = "Open on the workbench to see this app";
/** What the phone draws for a node type: its entry, or undefined for a type the phone has never heard of. */
export const phoneEntry = (type) => PHONE_CONTROLS[type];
/** The custom view on the phone: a sandboxed WebView speaking `shared/apps/customViewBridge.ts` under the same content policy. */
export const PHONE_CUSTOM_VIEW = {
    contract: "shared/apps/customViewBridge.ts",
    /**
     * react-native-webview props: JavaScript on and nothing else. `originWhitelist` is `["*"]` on purpose: an empty list hands every
     * navigation that is not on it to the system browser, so a view that set `location` to a URL carrying data would send it out. With
     * `["*"]` every navigation reaches `onShouldStartLoadWithRequest`, which refuses everything but the page itself (`about:blank`).
     */
    webview: { javaScriptEnabled: true, domStorageEnabled: false, allowFileAccess: false, allowUniversalAccessFromFileURLs: false, thirdPartyCookiesEnabled: false, sharedCookiesEnabled: false, setSupportMultipleWindows: false, originWhitelist: ["*"], mixedContentMode: "never" },
    /** The navigation gate: only the page itself loads; anything else — another URL, a new window, a file — is refused, never opened elsewhere. */
    onShouldStartLoadWithRequest: "allow only about:blank",
    /** The page is `srcdoc`-style HTML built the way the desktop builds it (shared/apps/customViewTheme.ts tokens + the content policy), loaded with `source={{ html }}`. */
    source: "html",
    /** Navigation away from the page is refused; so is every request the content policy would refuse. */
    navigation: "blocked",
};
/** The node types the phone has an entry for but the spec no longer has, and the reverse: both must be empty. */
export function phoneCoverageGaps() {
    const all = new Set([...BASE_NODE_TYPES, ...CONTROL_DEFS.map((d) => d.type)]);
    return { missing: [...all].filter((t) => !PHONE_CONTROLS[t]), extra: Object.keys(PHONE_CONTROLS).filter((t) => !all.has(t)) };
}
//# sourceMappingURL=phoneControls.js.map